import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import differenceWith from 'lodash/differenceWith';

import { usePipelineDataQuery, usePipelineHistoryQuery } from '../../graphql/query/pipelineQueries';
import { usePipelineTags } from '../../graphql/mutation/pipelineMutations';

import { usePipelineDeployment, usePipelineStepRemover } from '../../utils/hooks/use_pipeline_interactions';
import { getModelStepDataFromSteps } from '../../utils/product_utils';
import { loadNewPipelineVersionPage } from './pipelineHelpers';

import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

const PipelineDataContext = React.createContext({
  pipeline: null,
  pipelineSteps: [],
  deploymentID: null,
  latestPipelineDeployment: null,
  loading: false,
  error: null,
  pipelineHistory: null,
  pipelineHistoryError: null,
  pipelineHistoryLoading: false,
  isAnomalyDetectionEnabled: false,
  addPipelineTag: () => { },
  deletePipelineTag: () => { },
  deploy: () => { },
  undeploy: () => { },
  refetchPipelineHistory: () => { },
});

export const PipelineDataProvider = ({ children, enableAnomalyDetection = false }) => {
  const [isAnomalyDetectionEnabled, setIsAnomalyDetectionEnabled] = useState(enableAnomalyDetection);
  const history = useHistory();
  const routeParams = useParams();
  const currentPipelineSteps = useRef([]);

  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    loading,
    error,
    pipeline,
    refetch: refetchPipeline,
  } = usePipelineDataQuery(routeParams?.id, routeParams?.version)

  const {
    pipelineSteps,
    pipelineHistory,
    error: pipelineHistoryError,
    loading: pipelineHistoryLoading,
    latestPipelineDeployment,
    deploymentID,
    refetchHistoryQuery
  } = usePipelineHistoryQuery(routeParams?.id, routeParams?.version, currentWorkspaceId)

  const handleHistoryRefetch = useCallback((pipelineId, variantId) => {
    refetchPipeline()
    refetchHistoryQuery(pipelineId, variantId).then((res) => {
      loadNewPipelineVersionPage(res, history);
    });
  }, [history, refetchHistoryQuery, refetchPipeline])

  useEffect(() => {
    if (pipelineSteps) {
      currentPipelineSteps.current = [...pipelineSteps]
    }
  }, [pipelineSteps])


  const { addPipelineTag, deletePipelineTag } = usePipelineTags({
    pipelineId: routeParams?.id,
    pipeline,
  })

  const removeStep = usePipelineStepRemover(currentWorkspaceId, pipeline, pipelineSteps, handleHistoryRefetch);
  const { deploy, undeploy } = usePipelineDeployment();

  const pipelineID = pipeline?.id;

  const deployThis = useCallback((deploymentKey) => {
    const models = getModelStepDataFromSteps(pipelineSteps);

    /* This code would allow for reusing model configs from a specific deployment. However:
     * 1. The GQL query is incorrect, and returns MCs associated with the mapping table and not by pk_version_id.
     * 2. The MLOps API will attempt to reuse the most recent config for any model, which is currently a "good enough" heuristic.
     * const modelConfigs = latestPipelineDeployment ? latestPipelineDeployment.modelConfigs.map(mc => mc.model_config_id) : [];
     * const modelsWithoutConfigs = differenceWith(models, latestPipelineDeployment?.modelConfigs ?? [], (a, b) => a?.sha === b?.model_config?.model.sha)
     * deploy(deploymentKey, pipelineID, deploymentID, modelsWithoutConfigs, modelConfigs);
     */

    return deploy(deploymentKey, pipelineID, deploymentID, models, []);
  }, [deploy, deploymentID, pipelineID, pipelineSteps])

  const undeployThis = useCallback(undeployOptions => undeploy(undeployOptions, getModelStepDataFromSteps(pipelineSteps)), [undeploy, pipelineSteps])

  const removeStepFromPipeline = useCallback(async (index) => {
    // Triggers a new pipeline to be built
    removeStep(index);
  }, [removeStep])

  useEffect(() => {
    if (!pipelineSteps) {
      return;
    }

    const hasAnomalyDetection = pipelineSteps?.find(definitionStep => definitionStep?.Check?.tree?.flatMap((treeItemJSONStr) => {
      try {
        const treeObj = JSON.parse(treeItemJSONStr);
        return treeObj?.validations !== undefined;
      } catch {
        return false;
      }
    }))

    setIsAnomalyDetectionEnabled(hasAnomalyDetection);
  }, [pipelineSteps, routeParams.id]);
  
  const pipelineDataContextValue = useMemo(() => ({
    pipeline,
    pipelineSteps,
    deploymentID,
    latestPipelineDeployment,
    loading,
    error,
    pipelineHistory,
    pipelineHistoryError,
    pipelineHistoryLoading,
    isAnomalyDetectionEnabled,
    addPipelineTag,
    deletePipelineTag,
    removePipelineStep: removeStepFromPipeline,
    deploy: deployThis,
    undeploy: undeployThis,
    refetchPipelineHistory: handleHistoryRefetch,
  }), [
    pipeline,
    pipelineSteps,
    deploymentID,
    latestPipelineDeployment,
    loading,
    error,
    pipelineHistory,
    pipelineHistoryError,
    pipelineHistoryLoading,
    isAnomalyDetectionEnabled,
    addPipelineTag,
    deletePipelineTag,
    removeStepFromPipeline,
    deployThis,
    undeployThis,
    handleHistoryRefetch,
  ])

  return (
    <PipelineDataContext.Provider
      value={pipelineDataContextValue}
    >
      {children}
    </PipelineDataContext.Provider>
  );
};

export const usePipelineData = () => useContext(PipelineDataContext);

export default usePipelineData;
